import { useQueryClient } from '@tanstack/react-query';
import OilyticsClient from 'api/OilyticsClient';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { currentUserKey } from 'hooks/use-current-user';
import trim from 'lodash/trim';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface TimezoneSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTimezone?: string | null;
}

const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export function TimezoneSettingsDialog ({
  open,
  onOpenChange,
  currentTimezone,
}: TimezoneSettingsDialogProps) {
  const queryClient = useQueryClient();
  const browserTz = detectBrowserTimezone();
  const [value, setValue] = useState(currentTimezone || browserTz);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(currentTimezone || browserTz);
  }, [open, currentTimezone, browserTz]);

  const handleSave = async () => {
    const trimmed = trim(value);
    if (!trimmed) {
      toast.error('Timezone cannot be empty');
      return;
    }
    setIsSaving(true);
    try {
      const client = await OilyticsClient.create();
      await client.setUserTimezone(trimmed);
      // Refresh the cached user so consumers (Chat /generate body, scheduled
      // task form) see the new tz immediately.
      queryClient.invalidateQueries({ queryKey: currentUserKey });
      toast.success('Timezone updated');
      onOpenChange(false);
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message || (err as Error).message || 'Failed to update timezone';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Timezone</DialogTitle>
          <DialogDescription>
            Used for scheduled chatbot tasks and time-sensitive replies. IANA
            name (e.g. <code>America/New_York</code>).
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="user-tz">IANA timezone</Label>
            <Input
              id="user-tz"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="America/New_York"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setValue(browserTz)}
            disabled={value === browserTz}
          >
            Use browser timezone ({browserTz})
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
